import { useState, useRef, useEffect } from "react";
import { activate } from "../utils/keyboard-activate";
import { US_STATES } from "../constants/usStates";
import "./MultiStateSelect.css";

interface MultiStateSelectProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  max?: number;
  error?: string;
  disabled?: boolean;
}

export const MultiStateSelect = ({
  label,
  value,
  onChange,
  max = 3,
  error,
  disabled,
}: MultiStateSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filtered = US_STATES.filter(
    (s) =>
      s.toLowerCase().startsWith(search.toLowerCase()) && !value.includes(s)
  );

  const handleSelect = (state: string) => {
    if (value.length >= max) return;
    onChange([...value, state]);
    setSearch("");
    if (value.length + 1 >= max) {
      setOpen(false);
    }
  };

  const handleRemove = (state: string) => {
    onChange(value.filter((s) => s !== state));
  };

  return (
    <div ref={ref} className="mss">
      <label className="mss__label">{label}</label>
      <div
        className={`mss__trigger${error ? " mss__trigger--error" : ""}${disabled ? " mss__trigger--disabled" : ""}`}
        {...activate(() => {
          if (!disabled && value.length < max) setOpen(true);
        })}
      >
        <div className="mss__pills">
          {value.map((s) => (
            <span key={s} className="mss__pill">
              {s}
              {!disabled && (
                <button
                  type="button"
                  className="mss__pill-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(s);
                  }}
                  aria-label={`Remove ${s}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {value.length < max && !disabled && (
            <span className="mss__placeholder">
              {value.length === 0 ? "Select states..." : "+ Add"}
            </span>
          )}
        </div>
      </div>
      {error && <span className="mss__error">{error}</span>}

      {open && (
        <div className="mss__dropdown">
          <input
            ref={inputRef}
            className="mss__search"
            type="text"
            placeholder="Type to filter..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="mss__options">
            {filtered.length === 0 ? (
              <div className="mss__no-results">No states found</div>
            ) : (
              filtered.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="mss__option"
                  onMouseDown={() => handleSelect(s)}
                >
                  {s}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
